import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { PixelCalculatorService } from '../../components/default-uploader/services/pixel-calculator.service';
import { removeAll as removeAllFiles, removeFile, setFiles } from '../files/actions/files.actions';
import {
  removeAllFilesResolution,
  removeFileResolution,
  setFilesResolution,
  updateFilesResolution,
} from './files-resolution.actions';

@Injectable()
export class FilesResolutionEffects {
  constructor(private actions: Actions, private pixelCalculatorService: PixelCalculatorService) {}

  setFiles = createEffect(() => {
    return this.actions.pipe(
      ofType(setFiles),
      map(({ files }) => {
        const images = this.filterImages(files);
        const resolutionData = this.mapToResolutionData(images);
        return setFilesResolution({ files: images, resolutionData });
      }),
    );
  });

  calculateResolutionData = createEffect(() => {
    return this.actions.pipe(
      ofType(setFilesResolution),
      // `switchMap` restarts calculation for each new file set, tearing down the
      // previous `fileProcessed$` subscription so a stale run can't leak updates.
      // eslint-disable-next-line rxjs/no-unsafe-switchmap
      switchMap(({ files }) => {
        this.pixelCalculatorService.reset();
        this.pixelCalculatorService.calculate(files);
        return this.pixelCalculatorService.fileProcessed$.pipe(
          filter((fileResolution) => fileResolution.processed),
          map((fileResolution) =>
            updateFilesResolution({
              updates: [{ id: fileResolution.name, changes: fileResolution }],
            }),
          ),
        );
      }),
    );
  });

  removeAllFiles = createEffect(() => {
    return this.actions.pipe(
      ofType(removeAllFiles),
      map(() => {
        return removeAllFilesResolution();
      }),
    );
  });

  removeFile = createEffect(() => {
    return this.actions.pipe(
      ofType(removeFile),
      map(({ name }) => {
        return removeFileResolution({ name });
      }),
    );
  });

  stopResolutionDataCalculation = createEffect(
    () => {
      return this.actions.pipe(
        ofType(removeAllFilesResolution),
        tap(() => {
          this.pixelCalculatorService.reset();
        }),
      );
    },
    { dispatch: false },
  );

  private mapToResolutionData(images: File[]) {
    return images.map((image) => ({
      name: image.name,
      width: 0,
      height: 0,
      processed: false,
    }));
  }

  private filterImages(files: File[]) {
    return files.filter((file) => file.type.includes('image/'));
  }
}
